import * as Highcharts from 'highcharts'
import { commify, fixedNumber, units } from './utils'
import roundedCorners from 'highcharts-rounded-corners'

/**
 * Highcharts.relativeLength は highcharts-rounded-corners で使用するのだが、
 * Highchart 4.2.6 ではプロパティとして定義されていないので、
 * Highchart 5.x のソースから持ってきて定義する
 * v5.0.14: https://github.highcharts.com/355aac/highcharts.src.js
 * v4.2.6: https://github.highcharts.com/2bba08f/highcharts.src.js
 */
// eslint-disable-next-line no-import-assign
Highcharts.relativeLength = function (value, base, offset) {
  return /%$/.test(value) ? (base * parseFloat(value)) / 100 + (offset || 0) : parseFloat(value)
}
roundedCorners(Highcharts)

export const defaultSelectedIdx = 5

let selectedIdx = defaultSelectedIdx
let profitChart = null
let spChart = null

Highcharts.setOptions({
  lang: {
    numericSymbols: null,
    thousandsSep: '',
  },
})

export const selectedMap = (idx) => {
  switch (idx) {
    case 2:
      return 'veryBad'
    case 3:
      return 'bad'
    case 4:
      return 'littleBad'
    case 5:
      return 'median'
    case 6:
      return 'littleGood'
    case 7:
      return 'good'
    case 8:
      return 'veryGood'
    default:
      return null
  }
}

export const selectedNameMap = (idx) => {
  switch (idx) {
    case 2:
      return 'とても悪いケース'
    case 3:
      return '悪いケース'
    case 4:
      return 'すこし悪いケース'
    case 5:
      return '平均的なケース'
    case 6:
      return 'すこし良いケース'
    case 7:
      return '良いケース'
    case 8:
      return 'とても良いケース'
    default:
      return null
  }
}

export const colorMap = {
  plus: {
    normal: {
      veryBad: '#01a18e',
      bad: '#d0e074',
      littleBad: '#f5f58a',
      median: '#ffdd9d',
      littleGood: '#ffbf9d',
      good: '#ffbeca',
      veryGood: '#f98197',
    },
    hover: {
      veryBad: '#01755F',
      bad: '#B3CB45',
      littleBad: '#EDED5B',
      median: '#FFC670',
      littleGood: '#FF9B70',
      good: '#FF99AA',
      veryGood: '#F45269',
    },
  },
  minus: {
    normal: {
      veryBad: '#7682BB',
      bad: '#8699F6',
      littleBad: '#659EE8',
      median: '#55B2F0',
      littleGood: '#4DC7E6',
      good: '#3FB9CC',
      veryGood: '#3CAFA5',
    },
    hover: {
      veryBad: '#5662A2',
      bad: '#667AF1',
      littleBad: '#4780DD',
      median: '#3997E8',
      littleGood: '#32B1DA',
      good: '#289FB7',
      veryGood: '#259388',
    },
  },
  principal: {
    normal: '#9ecec5',
    hover: '#71B0A3',
  },
}

function selectedChart() {
  const band =
    selectedIdx == null
      ? []
      : [{ color: '#FFEBEE', from: selectedIdx - 0.5, to: selectedIdx + 0.5 }]
  // for profitchart
  if (profitChart) {
    profitChart.xAxis[0].update({ plotBands: band })
    profitChart.series[0].points.forEach((point, i) => {
      if (point.graphic) {
        if (i === 0 || i === 1) point.graphic.attr({ cursor: 'default' })
        else if (i !== selectedIdx) point.graphic.attr({ cursor: 'pointer' })
        else point.graphic.attr({ cursor: 'default' })
      }
    })
    profitChart.series[1].points.forEach((point, i) => {
      if (point.graphic) {
        if (i === 0 || i === 1) point.graphic.attr({ cursor: 'default' })
        else if (i !== selectedIdx) point.graphic.attr({ cursor: 'pointer' })
        else point.graphic.attr({ cursor: 'default' })
      }
    })
  }

  // for spChart
  if (spChart) {
    spChart.xAxis[0].update({ plotBands: band })
    spChart.series[0].points.forEach((point, i) => {
      if (point.graphic) {
        if (i === 0 || i === 1) point.graphic.attr({ cursor: 'default' })
        else if (i !== selectedIdx) point.graphic.attr({ cursor: 'pointer' })
        else point.graphic.attr({ cursor: 'default' })
      }
    })
    spChart.series[1].points.forEach((point, i) => {
      if (point.graphic) {
        if (i === 0 || i === 1) point.graphic.attr({ cursor: 'default' })
        else if (i !== selectedIdx) point.graphic.attr({ cursor: 'pointer' })
        else point.graphic.attr({ cursor: 'default' })
      }
    })
  }
}
function mouseOverChart(idx) {
  // for profitchart
  if (profitChart && idx !== selectedIdx && idx !== 0) {
    const caseStr = selectedMap(idx)
    if (caseStr != null) {
      const profit = profitChart.series[0].data[idx].actValue
      if (profit > 0) {
        profitChart.series[0].data[idx].update({
          color: colorMap.plus.hover[caseStr],
        })
      } else {
        profitChart.series[0].data[idx].update({
          color: colorMap.minus.hover[caseStr],
        })
      }
      profitChart.series[1].points[idx].color = colorMap.principal.hover
      profitChart.series[1].points[idx].update()
    }
  }
}
function mouseOutChart(idx, forceFlg) {
  // for profitchart
  if (profitChart && (forceFlg || selectedIdx !== idx)) {
    const caseStr = selectedMap(idx)
    if (caseStr != null) {
      const profit = profitChart.series[0].data[idx].actValue
      if (profit > 0) {
        profitChart.series[0].data[idx].update({
          color: colorMap.plus.normal[caseStr],
        })
      } else {
        profitChart.series[0].data[idx].update({
          color: colorMap.minus.normal[caseStr],
        })
      }
      profitChart.series[1].points[idx].color = colorMap.principal.normal
      profitChart.series[1].points[idx].update()
    }
  }
}

function calculateHeightPercentages(profitSeries) {
  const length = profitSeries[0].data.length
  const sums = []

  for (let i = 0; i < length; i++) {
    const firstValue = profitSeries[0].data[i]?.y || 0
    const secondValue = profitSeries[1].data[i]?.y || 0
    sums.push(firstValue + secondValue)
  }

  const maxSumIndex = sums.indexOf(Math.max(...sums))

  const maxFirstValue = profitSeries[0].data[maxSumIndex]?.y || 0
  const maxSecondValue = profitSeries[1].data[maxSumIndex]?.y || 0

  const heightPercentage = (maxSecondValue / (maxFirstValue + maxSecondValue)) * 100 || 0
  const inverseHeightPercentage = 100 - heightPercentage

  return { heightPercentage, inverseHeightPercentage }
}

export function makeProfitChart(id, profitSeries, sampleSeries, selectCallBack) {
  profitChart = null
  selectedIdx = defaultSelectedIdx

  const { heightPercentage, inverseHeightPercentage } = calculateHeightPercentages(profitSeries)

  // TODO チャートheightを投信アシストに合わせて小さくする
  const options = {
    chart: {
      type: 'column',
      marginLeft: 137,
      height: 350,
      backgroundColor: '#fdfbf7',
    },
    title: {
      // TODO 仮置き
      useHTML: true,
      align: 'left',
      style: {
        'background-color': '#e2f2ef',
        'border-radius': '20px',
        padding: '10px',
        fontSize: '14px',
        fontWeight: 'bold',
      },
      text: '棒グラフをクリック（タップ）するとそれぞれのケースの計算結果が表示されます',
      x: 40,
      y: 10,
    },
    credits: { enabled: false },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'top',
      layout: 'horizontal',
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '12px',
        color: '#43425D',
        cursor: 'default',
      },
      itemHoverStyle: {
        color: '#43425D',
      },
      y: 50,
    },
    xAxis: {
      labels: {
        enabled: true,
        formatter: function () {
          const caseStr = selectedMap(this.value)
          if (caseStr !== null) {
            const profit = profitSeries[0].data[this.value].actValue
            const principal = profitSeries[1].data[0].y
            if (profit !== undefined || principal !== undefined)
              // eslint-disable-next-line max-len
              return `<span style="color:#43425D;text-shadow:none;font-size:1.5rem;">評価額</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                profit + principal,
                false
              )}</span>`
          }
        },
      },
      tickLength: 0,
      lineColor: '#e8e8e8',
      plotBands: null,
    },
    yAxis: [
      { visible: false },
      {
        min: 0,
        gridLineColor: '#e8e8e8',
        labels: {
          style: {
            color: '#43425D',
          },
        },
        title: {
          text: '(サンプル数)',
          rotation: 0,
          align: 'high',
          offset: 57,
          style: {
            color: '#43425D',
          },
          y: 5,
        },
        height: heightPercentage + '%',
        top: inverseHeightPercentage + '%',
      },
    ],
    tooltip: { enabled: false },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        pointWidth: 75,
        borderWidth: 0,
        states: {
          hover: { enabled: false },
        },
        stacking: 'normal',
      },
      line: {
        states: {
          hover: { enabled: false },
        },
        enableMouseTracking: false,
        events: {
          legendItemClick: function () {
            return false
          },
        },
      },
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              selectedIdx = this.x
              const caseStr = selectedMap(selectedIdx)
              if (caseStr != null) {
                mouseOutChart(this.x, true)
                selectedChart()
                selectCallBack(this.x)
              }
            },
            mouseOver: function () {
              mouseOverChart(this.x)
            },
            mouseOut: function () {
              mouseOutChart(this.x, false)
            },
          },
        },
        dataLabels: {
          enabled: true,
          verticalAlign: 'top',
          crop: false,
          overflow: 'allow',
          useHTML: true,
          y: -55,
          formatter: function () {
            if (this.x === 0) {
              // eslint-disable-next-line max-len
              return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">投資総額</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                this.y,
                false
              )}</span></div>`
              // eslint-disable-next-line max-len
              // const label = `<p style="color:#43425D;text-shadow: 0 0 6px #FFFFFF, 0 0 3px #FFFFFF;">投資総額<br/>${this.y}</p>`
              // const chart = this.series.chart
              // console.log(this)
              // console.log(chart)
              // const categoryWidth = chart.plotWidth / chart.xAxis[0].dataMax
              // const offset = this.point.x * categoryWidth + categoryWidth / 2
              // const text = chart.renderer.text(label, -999, -999).add()

              // text.attr({
              //   x: chart.plotLeft + offset - text.getBBox().width / 2, //center label
              //   y: this.point.plotY - 8, // padding
              // })
            } else if (this.x === 1) {
              if (this.point.actValue !== undefined)
                // eslint-disable-next-line max-len
                return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">預金額</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                  this.point.actValue,
                  true
                )}</span></div>`
            } else if (this.point.actValue !== undefined) {
              const str = this.point.actValue > 0 ? '評価益' : '評価損'
              // eslint-disable-next-line max-len
              return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${str}</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                this.point.actValue,
                true
              )}</span></div>`
              // eslint-disable-next-line max-len
              // const label = `<p style="color:#43425D;text-shadow: 0 0 6px #FFFFFF, 0 0 3px #FFFFFF;">${str}<br/>${Math.abs(
              //   this.point.actValue
              // )}</p>`
              // const chart = this.series.chart
              // const text = chart.renderer.text(label, -999, -999).add()

              // text.attr({
              //   x: chart.plotLeft + this.point.plotX - text.getBBox().width / 2, //center label
              //   y: this.point.plotY - 8, // padding,
              // })
            }
          },
        },
      },
    },
    series: [...profitSeries, ...sampleSeries],
  }
  profitChart = Highcharts.chart(id, options)
  // profitChart.series[0].data.forEach((point, idx) => {
  //   if (point.dataLabel) {
  //     // const dataLabelY = point.dataLabel.y
  //     // const chartY = point.plotY
  //     // if (chartY < dataLabelY) {
  //     //   point.dataLabel.alignSetter({ y: -25 })
  //     // }
  //     // console.log(point.actValue+":"+point.yBottom+":"+point.plotY)
  //     // if (point.yBottom - point.plotY > 35) {
  //     //   point.update({
  //     //     dataLabels: { y: (point.yBottom - point.plotY) / 2 - 100 },
  //     //   })
  //     // }
  //   }
  // })
  selectedChart()
  return profitChart
}

export function makeSpChart(id, series, selectCallBack) {
  spChart = null
  selectedIdx = defaultSelectedIdx
  // TODO チャートheightを投信アシストに合わせて小さくする
  const options = {
    chart: {
      type: 'column',
      height: 450,
      marginTop: 20,
      marginBottom: 120,
      backgroundColor: '#fdfbf7',
    },
    title: {
      text: '',
    },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      labels: {
        enabled: true,
        formatter: function () {
          const caseStr = selectedMap(this.value)
          if (caseStr !== null) {
            const profit = series[0].data[this.value].actValue
            const principal = series[1].data[0].y
            if (profit !== undefined || principal !== undefined)
              // eslint-disable-next-line max-len
              return `<span style="color:#43425D;text-shadow:none;font-size:1.5rem;">評価額</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                profit + principal,
                false
              )}</span>`
          }
        },
      },
      tickLength: 0,
      lineColor: '#e8e8e8',
      plotBands: null,
    },
    yAxis: { visible: false },
    tooltip: {
      backgroundColor: '#fff',
      useHTML: true,
      positioner: function (labelWidth, labelHeight, point) {
        let tooltipX
        const chart = this.chart
        const _plotX = point.plotX
        const diffWidth = _plotX - chart.plotLeft
        const diffRatio = point.plotX / chart.plotSizeX
        if (_plotX - labelWidth < chart.plotLeft) {
          // 画面左のツールチップX軸
          if (diffWidth < labelWidth / 2) {
            tooltipX = _plotX - diffWidth
          } else {
            tooltipX = _plotX - labelWidth / 2
          }
        } else if (_plotX > chart.plotSizeX * 0.5 && _plotX - diffRatio * labelWidth > 0) {
          // 画面右のツールチップX軸
          tooltipX = _plotX - diffRatio * labelWidth
        } else {
          tooltipX = _plotX - labelWidth / 2
        }
        return {
          x: tooltipX,
          y: 330,
        }
      },
      formatter: function () {
        const caseStr = selectedNameMap(this.x)
        const formatter = new Intl.NumberFormat()
        if (caseStr != null) {
          const profit = this.points[0].point.actValue
          const principal = profit > 0 ? this.points[1].point.y : this.points[1].point.total
          const total = profit + principal
          const suffix = this.points[0].point.actValue > 0 ? '益' : '損'
          return `<div class="tooltip-inner"><div class="tooltip-inner-box">
            <p class="tooltip-ttl">${caseStr}</p>
            <hr color="${this.points[0].point.color}">
            <table><tr>
            <td><span class="symbol1"></span></td>
            <td>評価額:</td>
            <td>${formatter.format(total)} 円</td></tr><tr>
            <td class="total">
            <span class="symbol1"></span><span class="symbol2"></span>
            </td>
            <td>評価${suffix}:</td>
            <td>${formatter.format(this.points[0].point.actValue)} 円</td>
            </tr><tr>
            <td><span class="symbol2"></span></td><td>このケースに該当する割合:</td><td>
            ${((this.points[0].point.sample / 10000) * 100).toFixed(2)} %</td>
            </table>
            </div></div>`
        } else if (this.x === 0) {
          //投資総額
          const total = this.y
          return `<div class="tooltip-inner"><div class="tooltip-inner-box">
            <p class="tooltip-ttl">投資総額</p>
            <hr color="#9ecec5">
            <table><tr>
            <td><span class="symbol1"></span></td>
            <td></td>
            <td>${formatter.format(total)} 円</td></tr><tr>
            </table>
            </div></div>`
        } else if (this.x === 1) {
          // 預金額
          const total = this.points[0].point.actValue + this.points[1].point.y
          const suffix = this.points[0].point.actValue > 0 ? '益' : '損'
          return `<div class="tooltip-inner"><div class="tooltip-inner-box">
            <p class="tooltip-ttl">預金</p>
            <hr color="${this.points[0].point.color}">
            <table><tr>
            <td><span class="symbol1"></span></td>
            <td>評価額:</td>
            <td>${formatter.format(total)} 円</td></tr><tr>
            <td class="total">
            <span class="symbol1"></span><span class="symbol2"></span>
            </td>
            <td>評価${suffix}:</td>
            <td>${formatter.format(this.points[0].point.actValue)} 円</td>
            </table>
            </div></div>`
        } else {
          return false
        }
      },
      shared: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        states: {
          hover: { enabled: false },
        },
        stacking: 'normal',
      },
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              selectedIdx = this.x
              const caseStr = selectedMap(selectedIdx)
              if (caseStr != null) {
                mouseOutChart(this.x, true)
                selectedChart()
                selectCallBack(this.x)
              }
            },
            mouseOver: function () {
              mouseOverChart(this.x)
            },
            mouseOut: function () {
              mouseOutChart(this.x, false)
            },
          },
        },
        dataLabels: {
          enabled: true,
          verticalAlign: 'top',
          crop: false,
          overflow: 'allow',
          useHTML: true,
          y: -45,
          formatter: function () {
            if (this.x === 0) {
              // eslint-disable-next-line max-len
              return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.3rem;">投資総額</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                this.y,
                false
              )}</span></div>`
            } else if (this.x === 1) {
              if (this.point.actValue !== undefined)
                // eslint-disable-next-line max-len
                return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.3rem;">預金</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                  this.point.actValue,
                  true
                )}</span></div>`
            } else if (this.point.actValue !== undefined) {
              const str = this.point.actValue > 0 ? '評価益' : '評価損'
              // eslint-disable-next-line max-len
              return `<div style="text-align:center;"><span style="color:#43425D;text-shadow:none;font-size:1.3rem;">${str}</span><br/><span style="color:#43425D;text-shadow:none;font-size:1.5rem;">${formatValue(
                this.point.actValue,
                true
              )}</span></div>`
            }
          },
        },
      },
    },
    series: series,
  }
  spChart = Highcharts.chart(id, options)
  selectedChart()
  return spChart
}

export function resetChart() {
  if (profitChart) {
    profitChart.destroy()
    profitChart = null
  }
  if (spChart) {
    spChart.destroy()
    spChart = null
  }
}

function formatValue(value, signFlg) {
  const sign = signFlg && value > 0 ? '+' : ''
  if (value > units.trillionYen.value) {
    return `${sign}${commify(fixedNumber(value / units.trillionYen.value, 1))}${
      units.trillionYen.str
    }`
  } else if (value > units.hundredMillionYen.value) {
    return `${sign}${commify(fixedNumber(value / units.hundredMillionYen.value, 1))}${
      units.hundredMillionYen.str
    }`
  } else if (value < units.tenThousandYen.value && value > -1 * units.tenThousandYen.value) {
    return `${sign}${commify(fixedNumber(value, 0))}${units.yen.str}`
  } else {
    return `${sign}${commify(fixedNumber(value / units.tenThousandYen.value, 0))}${
      units.tenThousandYen.str
    }`
  }
}
